import { useState } from 'preact/hooks';
import { useCreditsStore, formatCredits } from '../state/creditsStore';
import { createCreditsBuyIntent, type CreditsProduct } from '../api/billing';
import { VatNotice } from './VatNotice';

// ════════════════════════════════════════════════════════════════════════════
// TopUpModal — pure renderer of the orthogonal top-up-modal region (F3).
// Opens via the store: user tap (BalancePill → openTopUpModal(null)) OR a
// stage-5 gate denial (FEATURE_GATED reason:'insufficient_balance' →
// onFeatureGated → openTopUpModal(featureKey)). The store owns open/closed;
// this component only renders it and dispatches the buy-intent redirect.
//
// PACKS mirror backend CREDIT_PACKS (credits-ledger.service.ts) — EUR 5/15/40/100
// with +0/+5/+10/+20% bonus. Each card POSTs the credits buy-intent and redirects
// to the returned edge checkout URL. Follows the hcem-* modal convention.
//
// Stripe Tax P3: the shared VatNotice states the VAT basis at the buy buttons ("VAT is determined by your billing
// country at checkout") — a notice, not a consent gate (setpoint-evals/stripe-tax-p3/SE-03). Pure rendering:
// no store, no FSM row, no change to the buy flow.
// ════════════════════════════════════════════════════════════════════════════

interface Pack {
  product: CreditsProduct;
  eur: number;
  paidCenti: number;
  bonusCenti: number;
}

// Mirror of backend CREDIT_PACKS (1 credit = 100 centicredits; peg 1 credit = €0.10).
const PACKS: Pack[] = [
  { product: 'valko_credits_5', eur: 5, paidCenti: 5000, bonusCenti: 0 },
  { product: 'valko_credits_15', eur: 15, paidCenti: 15000, bonusCenti: 750 },
  { product: 'valko_credits_40', eur: 40, paidCenti: 40000, bonusCenti: 4000 },
  { product: 'valko_credits_100', eur: 100, paidCenti: 100000, bonusCenti: 20000 },
];

export function TopUpModal() {
  const open = useCreditsStore((s) => s.topUpModalOpen);
  const gatedFeatureKey = useCreditsStore((s) => s.gatedFeatureKey);
  const closeTopUpModal = useCreditsStore((s) => s.closeTopUpModal);
  const activeUserId = useCreditsStore((s) => s.activeUserId);
  const markTopupInitiated = useCreditsStore((s) => s.markTopupInitiated);
  const [busy, setBusy] = useState<CreditsProduct | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const buy = async (product: CreditsProduct) => {
    setError(null);
    setBusy(product);
    try {
      const { url } = await createCreditsBuyIntent(product);
      // Persist the origin-keyed topup marker BEFORE the hard-nav (gate a of the
      // topup-confirmation): this component's state is destroyed by window.location.href;
      // only the persisted store survives the round-trip to Stripe and back. packCenti =
      // total credits added (paid + bonus) — the number the success toast shows.
      const pack = PACKS.find((p) => p.product === product);
      if (activeUserId && pack) {
        markTopupInitiated({ userId: activeUserId, packCenti: pack.paidCenti + pack.bonusCenti });
      }
      window.location.href = url; // hand off to the edge checkout
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout. Please try again.');
      setBusy(null);
    }
  };

  return (
    <div class="tum-backdrop" onClick={closeTopUpModal} role="dialog" aria-modal="true" aria-labelledby="tum-title">
      <div class="tum-card" onClick={(e) => e.stopPropagation()}>
        <button class="tum-close" onClick={closeTopUpModal} type="button" aria-label="Close">×</button>
        <h3 id="tum-title" class="tum-title">Top up credits</h3>
        {gatedFeatureKey ? (
          <p class="tum-gate">You're out of credits for this action. Add a pack to continue.</p>
        ) : (
          <p class="tum-sub">Prepaid credits — no subscription. Larger packs include bonus credits.</p>
        )}

        <div class="tum-packs">
          {PACKS.map((p) => {
            const totalCenti = p.paidCenti + p.bonusCenti;
            const bonusPct = p.paidCenti > 0 ? Math.round((p.bonusCenti / p.paidCenti) * 100) : 0;
            return (
              <button
                key={p.product}
                class="tum-pack"
                type="button"
                disabled={busy != null}
                onClick={() => buy(p.product)}
              >
                <span class="tum-pack__eur">€{p.eur}</span>
                <span class="tum-pack__credits">{formatCredits(totalCenti)} cr</span>
                {bonusPct > 0 && <span class="tum-pack__bonus">+{bonusPct}% bonus</span>}
                {busy === p.product && <span class="tum-pack__busy" aria-hidden="true">…</span>}
              </button>
            );
          })}
        </div>

        <VatNotice center />
        {error && <p class="tum-error" role="alert">{error}</p>}
        <p class="tum-help">You'll be redirected to secure checkout. Credits are added on payment.</p>
      </div>
    </div>
  );
}
